import { useEffect } from 'react';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogUrl?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  canonicalUrl?: string;
  schema?: object;
}

export function SEO({
  title = 'RaiseBeacon - Find Investors & Raise Capital',
  description = 'RaiseBeacon helps founders find the right investors and raise capital. Connect with curated investors across 30 cities and 33 industries.',
  keywords = 'investors, venture capital, angel investors, startup funding, raise capital, VC, startup investors, funding, seed funding, series A',
  ogTitle,
  ogDescription,
  ogImage = 'https://backr.us/logo-backr-full.png',
  ogUrl,
  twitterTitle,
  twitterDescription,
  twitterImage = 'https://backr.us/logo-backr-full.png',
  canonicalUrl,
  schema,
}: SEOProps) {
  useEffect(() => {
    // Update basic meta tags
    document.title = title;
    
    const setMetaTag = (name: string, content: string, property = false) => {
      const selector = property ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let element = document.querySelector(selector) as HTMLMetaElement;
      if (!element) {
        element = document.createElement('meta');
        if (property) {
          element.setAttribute('property', name);
        } else {
          element.setAttribute('name', name);
        }
        document.head.appendChild(element);
      }
      element.content = content;
    };

    // Basic SEO
    setMetaTag('description', description);
    setMetaTag('keywords', keywords);

    // Open Graph
    setMetaTag('og:title', ogTitle || title, true);
    setMetaTag('og:description', ogDescription || description, true);
    setMetaTag('og:image', ogImage, true);
    if (ogUrl) {
      setMetaTag('og:url', ogUrl, true);
    }

    // Twitter Card
    setMetaTag('twitter:title', twitterTitle || ogTitle || title);
    setMetaTag('twitter:description', twitterDescription || ogDescription || description);
    setMetaTag('twitter:image', twitterImage || ogImage);

    // Canonical URL
    if (canonicalUrl) {
      let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.rel = 'canonical';
        document.head.appendChild(canonical);
      }
      canonical.href = canonicalUrl;
    }

    // JSON-LD Schema
    if (schema) {
      const schemaId = 'dynamic-schema';
      let existingSchema = document.getElementById(schemaId) as HTMLScriptElement;
      if (!existingSchema) {
        existingSchema = document.createElement('script');
        existingSchema.id = schemaId;
        existingSchema.type = 'application/ld+json';
        document.head.appendChild(existingSchema);
      }
      existingSchema.textContent = JSON.stringify(schema);
    }

    // Cleanup function
    return () => {
      // Remove dynamic schema on unmount
      const dynamicSchema = document.getElementById('dynamic-schema');
      if (dynamicSchema) {
        dynamicSchema.remove();
      }
    };
  }, [title, description, keywords, ogTitle, ogDescription, ogImage, ogUrl, twitterTitle, twitterDescription, twitterImage, canonicalUrl, schema]);

  return null;
}

// Predefined SEO configurations for common pages
export const seoConfigs = {
  home: {
    title: 'RaiseBeacon - Find Investors & Raise Capital',
    description: 'RaiseBeacon helps founders find the right investors and raise capital. Connect with curated investors across 30 cities and 33 industries.',
    keywords: 'investors, venture capital, angel investors, startup funding, raise capital, VC, startup investors',
  },
  search: {
    title: 'Search Investors - RaiseBeacon',
    description: 'Search our curated investor network. Filter by location, industry, investment stage, and more.',
    keywords: 'investor search, find investors, VC search, angel investor database',
  },
  pricing: {
    title: 'Pricing - RaiseBeacon',
    description: 'Choose the plan that works for your fundraising needs. Access premium investor data and outreach tools.',
    keywords: 'investor database pricing, startup funding tools, investor contact database',
  },
  privacy: {
    title: 'Privacy Policy - RaiseBeacon',
    description: 'RaiseBeacon privacy policy and data protection information.',
    keywords: 'privacy policy, data protection',
  },
  terms: {
    title: 'Terms of Service - RaiseBeacon',
    description: 'RaiseBeacon terms of service and usage agreement.',
    keywords: 'terms of service, terms and conditions',
  },
};

// Helper function to generate investor profile schema
export function generateInvestorSchema(investor: {
  name: string;
  firm: string;
  role?: string;
  location?: string;
  industries?: string[];
  stage?: string;
  linkedin?: string;
  website?: string;
  bio?: string;
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: investor.name,
    jobTitle: investor.role || 'Investor',
    worksFor: {
      '@type': 'Organization',
      name: investor.firm,
      url: investor.website,
    },
    address: investor.location ? {
      '@type': 'PostalAddress',
      addressLocality: investor.location,
    } : undefined,
    description: investor.bio,
    sameAs: investor.linkedin ? [investor.linkedin] : undefined,
    knowsAbout: investor.industries,
  };
}

// Helper function to generate location page schema
export function generateLocationSchema(location: string, investorCount: number) {
  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${location} Investors - RaiseBeacon`,
    description: `Find ${investorCount}+ verified investors in ${location}. Connect with VCs, angels, and accelerators in your area.`,
    url: `https://backr.us/#/search?location=${encodeURIComponent(location)}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: [],
    },
  };
}

// Helper function to generate industry page schema
export function generateIndustrySchema(industry: string, investorCount: number) {
  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${industry} Investors - RaiseBeacon`,
    description: `Find ${investorCount}+ ${industry} investors. Connect with VCs and angels specializing in ${industry} startups.`,
    url: `https://backr.us/#/search?industry=${encodeURIComponent(industry)}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: [],
    },
  };
}

export default SEO;
